"use client";

import { cn } from "@/utils/cn";
import React from "react";

export const AppStoreButtons: React.FC<{ className?: string }> = ({ className = "justify-start" }) => {
	return (
		<div className={cn("flex flex-col sm:flex-row gap-4 items-center", className)}>
			{/* Google Play Store Button */}
			<a
				href="https://play.google.com/store/apps/details?id=in.settlin.app"
				target="_blank"
				rel="noopener noreferrer"
				className={cn(
					"group inline-flex items-center justify-start gap-3",
					"bg-black text-white font-medium rounded-xl border border-white/20",
					"h-[60px] w-full sm:w-[180px] px-4",
					"hover:bg-black/90 hover:border-white/40",
					"transition-all duration-300 hover:-translate-y-1 active:translate-y-0",
					"focus:outline-none focus:ring-2 focus:ring-white/30",
					"animate-fade-in-up",
				)}
				style={{ animationDelay: "0.4s" }}
				aria-label="Download Settlin app from Google Play Store"
			>
				<span className="w-8 h-8 flex-shrink-0 transition-transform group-hover:scale-110">
					<svg viewBox="0 0 24 24" fill="none" className="w-full h-full">
						<path
							d="M3 20.5V3.5C3 2.91 3.34 2.39 3.84 2.15L13.69 12L3.84 21.85C3.34 21.6 3 21.09 3 20.5Z"
							fill="url(#playGradient1)"
						/>
						<path d="M16.81 15.12L6.05 21.34L14.54 12.85L16.81 15.12Z" fill="url(#playGradient2)" />
						<path d="M16.81 8.88L14.54 11.15L6.05 2.66L16.81 8.88Z" fill="url(#playGradient3)" />
						<path
							d="M6.05 21.34L16.81 15.12L20.16 13.06C20.67 12.74 21 12.39 21 12S20.67 11.26 20.16 10.94L16.81 8.88L14.54 11.15L13.69 12L14.54 12.85L16.81 15.12Z"
							fill="url(#playGradient4)"
						/>
						<defs>
							<linearGradient id="playGradient1" x1="9.375" y1="0" x2="9.375" y2="24" gradientUnits="userSpaceOnUse">
								<stop stopColor="#047ED6" />
								<stop offset="1" stopColor="#50E6FF" />
							</linearGradient>
							<linearGradient
								id="playGradient2"
								x1="11.43"
								y1="17.09"
								x2="11.43"
								y2="17.09"
								gradientUnits="userSpaceOnUse"
							>
								<stop stopColor="#FFDA1C" />
								<stop offset="1" stopColor="#FEB705" />
							</linearGradient>
							<linearGradient
								id="playGradient3"
								x1="11.43"
								y1="6.91"
								x2="11.43"
								y2="6.91"
								gradientUnits="userSpaceOnUse"
							>
								<stop stopColor="#33C481" />
								<stop offset="1" stopColor="#61E3A7" />
							</linearGradient>
							<linearGradient id="playGradient4" x1="17.525" y1="12" x2="17.525" y2="12" gradientUnits="userSpaceOnUse">
								<stop stopColor="#D9414F" />
								<stop offset="1" stopColor="#8C193F" />
							</linearGradient>
						</defs>
					</svg>
				</span>
				<div className="flex flex-col items-start justify-center">
					<span className="text-xs text-white/80 leading-none font-normal">GET IT ON</span>
					<span className="text-base font-semibold leading-tight mt-0.5">Google Play</span>
				</div>
			</a>

			{/* App Store Button */}
			<a
				href="https://apps.apple.com/in/app/settlin/id6443720499"
				target="_blank"
				rel="noopener noreferrer"
				className={cn(
					"group inline-flex items-center justify-start gap-3",
					"bg-black text-white font-medium rounded-xl border border-white/20",
					"h-[60px] w-full sm:w-[180px] px-4",
					"hover:bg-black/90 hover:border-white/40",
					"transition-all duration-300 hover:-translate-y-1 active:translate-y-0",
					"focus:outline-none focus:ring-2 focus:ring-white/30",
				)}
				style={{ animationDelay: "0.5s" }}
				aria-label="Download Settlin app from Apple App Store"
			>
				<span className="w-8 h-8 flex-shrink-0 transition-transform group-hover:scale-110">
					<svg viewBox="0 0 24 24" fill="none" className="w-full h-full">
						<path
							fill="currentColor"
							d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"
						/>
					</svg>
				</span>
				<div className="flex flex-col items-start justify-center">
					<span className="text-xs text-white/80 leading-none font-normal">Download on the</span>
					<span className="text-base font-semibold leading-tight mt-0.5">App Store</span>
				</div>
			</a>
		</div>
	);
};
